import { FC, useCallback, useContext, useMemo } from "react";
import VisibilitySensor from "react-visibility-sensor";
import { VirtualizedTable } from "@components/TableNew";
import { TableColumnsContext } from "@components/TableNew/TableColumns";
import TableSortingContext, { TableSortingValue, SortingOption } from "@components/TableNew/TableSorting";
import { Sort } from "@models";
import { useMenuExampleTableConfig, MENU_EXAMPLE_SORT_KEYS } from "./MenuExampleTableConfig";
import { GlobalContext } from "@components/App/App.context";

const SORTING_OPTIONS: SortingOption[] = [
    { id: 2, name: 'menuItemName', defaultDirection: Sort.Asc },
    { id: 4, name: 'price', defaultDirection: Sort.Asc },
    { id: 1, name: 'restaurantName', defaultDirection: Sort.Asc },
];

export interface MenuExampleTableProps {
    data: any[];
    isLoading: boolean;
    totalCount: number;
    onLoadMore: () => void;
    sortColumn: number;
    sortDirection: string;
    onSortChange: (columnIndex: number) => void;
}

const MenuExampleTable: FC<MenuExampleTableProps> = ({
    data,
    isLoading,
    totalCount,
    onLoadMore,
    sortColumn,
    sortDirection,
    onSortChange,
}) => {
    const rowsCount = isLoading ? Math.max(30, data.length) : data.length;
    const { getRem } = useContext(GlobalContext);

    const baseColumns = useMenuExampleTableConfig(data);

    const columns = useMemo(
        () => baseColumns.map((col, index) => ({
            ...col,
            sortKey: MENU_EXAMPLE_SORT_KEYS[index],
        })),
        [baseColumns]
    );

    const columnsContextValue = useMemo(() => ({ data: columns }), [columns]);

    const sortingValue: TableSortingValue = useMemo(() => ({
        id: sortColumn,
        direction: sortDirection === 'asc' ? Sort.Asc : Sort.Desc,
    }), [sortColumn, sortDirection]);

    const handleSortChange = useCallback((newValue: TableSortingValue) => {
        onSortChange(newValue.id as number);
    }, [onSortChange]);

    const sortingContextValue = useMemo(() => ({
        value: sortingValue,
        options: SORTING_OPTIONS,
        setValue: handleSortChange,
    }), [sortingValue, handleSortChange]);

    const hasMore = data.length < totalCount;

    const handleVisibilityChange = (isVisible: boolean) => {
        if (!isLoading && isVisible && hasMore) {
            onLoadMore();
        }
    };

    return (
        <TableSortingContext.Provider value={sortingContextValue}>
            <TableColumnsContext.Provider value={columnsContextValue}>
                <VirtualizedTable
                    rowsCount={rowsCount}
                    isLoading={isLoading}
                    rowHeight={getRem(60)}
                    showFallback
                    testId="menuExamplesTable"
                >
                    {hasMore && (
                        <VisibilitySensor
                            onChange={handleVisibilityChange}
                            partialVisibility={data.length > 0}
                        >
                            <div className="MenuExamplesPopup__table-loader" />
                        </VisibilitySensor>
                    )}
                </VirtualizedTable>
            </TableColumnsContext.Provider>
        </TableSortingContext.Provider>
    );
};

export default MenuExampleTable;
